import { useState, useEffect, useCallback } from "react";
import {
  User,
  Mail,
  Phone,
  Briefcase,
  Calendar,
  CreditCard,
  ChevronLeft,
  Building2,
  MapPin,
} from "lucide-react";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useLocation } from "react-router-dom";
import { SaveEmployee, SaveAccessCard } from "../../../services/employee";
import { DropdownJobTitle, fetchdropdownMall, fetchdropdownTenant } from "../../../services/dropdown";
import { GetEmployee } from "../../../services/employee";
import { JobTitleOption } from "../../../types/dropdown";
import { toLocalISOString } from "../../../utils";

const INITIAL_VALUES: {
  TenantEmployeeId: number;
  TenantId: number;
  MallId: number;
  BrandName: string;
  EmployeeNumber: string;
  CardExpiredOn: Date | null;
  IDNumber: string;
  FirstName: string;
  LastName: string;
  FullName: string;
  Gender: number;
  BirthOn: Date | null;
  PhoneNumber: string;
  Email: string;
  Address: string;
  EmployeeOn: Date | null;
  Status: number;
} = {
  TenantEmployeeId: 0,
  TenantId: 0,
  MallId: 0,
  BrandName: "",
  EmployeeNumber: "",
  CardExpiredOn: null,
  IDNumber: "",
  FirstName: "",
  LastName: "",
  FullName: "",
  Gender: 1,
  BirthOn: null,
  PhoneNumber: "",
  Email: "",
  Address: "",
  EmployeeOn: null,
  Status: 1,
};

const INITIAL_ERRORS = {
  FirstName: "",
  LastName: "",
  Email: "",
  PhoneNumber: "",
  IDNumber: "",
  EmployeeNumber: "",
  JobTitle: "",
};

const inputClass = `w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-10 py-2.5 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`;
const inputErrorClass = `w-full bg-white dark:bg-gray-800 border border-red-400 dark:border-red-500 rounded-lg px-10 py-2.5 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent transition-all`;
const datePickerClass = `w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-10 py-2.5 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`;

interface DropdownOption {
  value: number;
  label: string;
}

export default function TenantEmployeeForm() {
  const { state } = useLocation();
  const { selectedMall, selectedTenant, employeeId } = state || {};
  const [jobTitleOptions, setJobTitleOptions] = useState<JobTitleOption[]>([]);
  const [selectedJobTitle, setSelectedJobTitle] = useState("0");
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [originalEmployeeNumber, setOriginalEmployeeNumber] = useState("");

  const [mallOptions, setMallOptions] = useState<DropdownOption[]>([]);
  const [tenantOptions, setTenantOptions] = useState<DropdownOption[]>([]);
  const [selectedMallId, setSelectedMallId] = useState("0");
  const [selectedTenantId, setSelectedTenantId] = useState("0");
  const [isTenantLoading, setIsTenantLoading] = useState(false);

  const navigate = useNavigate();
  const [formData, setFormData] = useState(INITIAL_VALUES);

  const todayPlaceholder = `e.g. ${new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date())}`;

  const fetchTenantEmployee = useCallback(
    async (
      tenantemployeeid: number,
      employeenumber: string,
      tenantid: number,
      mallId: number,
      number: number,
      page: number
    ) => {
      try {
        const response = await GetEmployee(
          tenantemployeeid,
          employeenumber,
          tenantid,
          mallId,
          number,
          page
        );
        if (response.Code === 0 && response.tenantEmployeeDatas.length > 0) {
          const employee = response.tenantEmployeeDatas[0];

          setFormData({
            TenantEmployeeId: employee.TenantEmployeeId,
            TenantId: employee.TenantId || 0,
            MallId: employee.MallId || 0,
            BrandName: employee.BrandName || "",
            EmployeeNumber: employee.EmployeeNumber,
            CardExpiredOn: employee.CardExpiredOn ? new Date(employee.CardExpiredOn) : null,
            IDNumber: employee.IDNumber,
            FirstName: employee.FirstName,
            LastName: employee.LastName,
            FullName: employee.FullName || employee.FirstName + " " + employee.LastName,
            Gender: employee.Gender,
            BirthOn: employee.BirthOn ? new Date(employee.BirthOn) : null,
            PhoneNumber: employee.PhoneNumber,
            Email: employee.Email,
            Address: employee.Address,
            EmployeeOn: employee.EmployeeOn ? new Date(employee.EmployeeOn) : null,
            Status: employee.Status !== undefined ? Number(employee.Status) : 1,
          });

          if (employee.MallId) {
            setSelectedMallId(String(employee.MallId));
            await fetchTenantsByMall(employee.MallId);
          }
          if (employee.TenantId) {
            setSelectedTenantId(String(employee.TenantId));
          }

          setOriginalEmployeeNumber(employee.EmployeeNumber);
          setSelectedJobTitle(String(employee.JobTitleId || "0"));
        }
      } catch {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Failed to fetch employee data",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    },
    []
  );

  const fetchMalls = async () => {
    try {
      const response = await fetchdropdownMall(0);
      if (response?.Code === 0) {
        const options = response.dropdownClassDatas.map(
          (item: { DataId: number; DataLabel: string }) => ({
            value: item.DataId,
            label: item.DataLabel,
          })
        );
        setMallOptions(options);
      }
    } catch {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to fetch mall list",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
  };

  const fetchTenantsByMall = async (mallId: number) => {
    setIsTenantLoading(true);
    setTenantOptions([]);
    try {
      const response = await fetchdropdownTenant(mallId);
      if (response?.Code === 0) {
        const options = response.dropdownClassDatas.map(
          (item: { DataId: number; DataLabel: string }) => ({
            value: item.DataId,
            label: item.DataLabel,
          })
        );
        setTenantOptions(options);
      }
    } catch {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to fetch tenant list",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setIsTenantLoading(false);
    }
  };

  const handleMallChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const mallId = e.target.value;
    setSelectedMallId(mallId);
    setSelectedTenantId("0");
    setFormData((prev) => ({ ...prev, MallId: Number(mallId), TenantId: 0 }));

    if (mallId && mallId !== "0") {
      await fetchTenantsByMall(Number(mallId));
    } else {
      setTenantOptions([]);
    }
  };

  const handleTenantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const tenantId = e.target.value;
    setSelectedTenantId(tenantId);
    setFormData((prev) => ({ ...prev, TenantId: Number(tenantId) }));
  };

  useEffect(() => {
    fetchMalls();
  }, []);

  useEffect(() => {
    if (employeeId) {
      fetchTenantEmployee(employeeId, "", selectedTenant, 0, 0, 1);
    }
  }, [employeeId, selectedTenant, fetchTenantEmployee]);

  useEffect(() => {
    if (!employeeId) {
      if (selectedMall) {
        const mallIdStr = String(selectedMall);
        setSelectedMallId(mallIdStr);
        setFormData((prev) => ({ ...prev, MallId: Number(selectedMall) }));
        fetchTenantsByMall(Number(selectedMall));
      }
      if (selectedTenant) {
        setSelectedTenantId(String(selectedTenant));
        setFormData((prev) => ({ ...prev, TenantId: Number(selectedTenant) }));
      }
    }
  }, [employeeId, selectedMall, selectedTenant]);

  useEffect(() => {
    if (mallOptions.length === 1 && selectedMallId === "0") {
      const mallId = mallOptions[0].value;
      setSelectedMallId(String(mallId));
      setFormData((prev) => ({ ...prev, MallId: mallId }));
      fetchTenantsByMall(mallId);
    }
  }, [mallOptions, selectedMallId]);

  useEffect(() => {
    if (tenantOptions.length === 1 && selectedTenantId === "0") {
      const tenantId = tenantOptions[0].value;
      setSelectedTenantId(String(tenantId));
      setFormData((prev) => ({ ...prev, TenantId: tenantId }));
    }
  }, [tenantOptions, selectedTenantId]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      FullName: (prev.FirstName + " " + prev.LastName).trim(),
    }));
  }, [formData.FirstName, formData.LastName]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setFormData((prev) => {
      let newValue: string | Date = value;
      if (["BirthOn", "CardExpiredOn", "EmployeeOn"].includes(name))
        newValue = new Date(value);
      return { ...prev, [name]: newValue };
    });
  };

  const fetchJobTitle = async (referenceId: number) => {
    try {
      const response = await DropdownJobTitle(referenceId);
      if (response.Code === 0) {
        const options = response.dropdownClassDatas.map(
          (item: { DataId: number; DataLabel: string }) => ({
            value: item.DataId,
            label: item.DataLabel,
          })
        );
        const filteredOptions = options.filter(
          (option: JobTitleOption) => option.value !== 10 && option.value !== 20
        );
        setJobTitleOptions(filteredOptions);
      }
    } catch {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to fetch job title",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      setJobTitleOptions([]);
    }
  };

  useEffect(() => {
    fetchJobTitle(0);
  }, []);

  const validate = (): boolean => {
    const newErrors = { ...INITIAL_ERRORS };
    let valid = true;

    if (!formData.FirstName.trim()) {
      newErrors.FirstName = "First Name is required";
      valid = false;
    }
    if (!formData.LastName.trim()) {
      newErrors.LastName = "Last Name is required";
      valid = false;
    }
    if (!formData.Email.trim()) {
      newErrors.Email = "Email is required";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)) {
      newErrors.Email = "Email format is invalid";
      valid = false;
    }
    if (!formData.PhoneNumber.trim()) {
      newErrors.PhoneNumber = "Phone Number is required";
      valid = false;
    }
    if (!formData.IDNumber.trim()) {
      newErrors.IDNumber = "NIK is required";
      valid = false;
    }
    if (!formData.EmployeeNumber.trim()) {
      newErrors.EmployeeNumber = "Employee Card Number is required";
      valid = false;
    }
    if (!selectedJobTitle || selectedJobTitle === "0") {
      newErrors.JobTitle = "Job Title is required";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const isEdit = !!employeeId;
    const cardNumberChanged =
      isEdit && formData.EmployeeNumber !== originalEmployeeNumber;

    if (cardNumberChanged) {
      const confirm = await Swal.fire({
        icon: "question",
        title: "Card Number Changed",
        text: `Employee card number has been changed from "${originalEmployeeNumber}" to "${formData.EmployeeNumber}". Are you sure?`,
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, change it",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return;
    }

    const employeePayload = {
      tenantEmployeeData: {
        ...(formData.TenantEmployeeId && {
          TenantEmployeeId: formData.TenantEmployeeId,
        }),
        TenantId: selectedTenantId !== "0" ? Number(selectedTenantId) : selectedTenant?.value,
        MallId: formData.MallId,
        BrandName: formData.BrandName,
        EmployeeNumber: formData.EmployeeNumber,
        CardExpiredOn: formData.CardExpiredOn ? toLocalISOString(formData.CardExpiredOn) : "",
        IDNumber: formData.IDNumber,
        FirstName: formData.FirstName,
        LastName: formData.LastName,
        FullName: formData.FirstName + " " + formData.LastName,
        Gender: formData.Gender,
        BirthOn: formData.BirthOn ? toLocalISOString(formData.BirthOn) : "",
        PhoneNumber: formData.PhoneNumber,
        Email: formData.Email,
        Address: formData.Address || "",
        JobTitleId: Number(selectedJobTitle),
        EmployeeOn: formData.EmployeeOn ? toLocalISOString(formData.EmployeeOn) : "",
        Status: Number(formData.Status),
      },
    };

    const accessCardPayload = {
      accessCardDatas: [
        {
          TenantEmployeeId: formData.TenantEmployeeId,
          TenantId: selectedTenantId !== "0" ? Number(selectedTenantId) : selectedTenant?.value,
          MallId: formData.MallId,
          BrandName: formData.BrandName,
          EmployeeNumber: formData.EmployeeNumber,
          CardExpiredOn: formData.CardExpiredOn ? toLocalISOString(formData.CardExpiredOn) : "",
          IDNumber: formData.IDNumber,
          FirstName: formData.FirstName,
          LastName: formData.LastName,
          FullName: formData.FirstName + " " + formData.LastName,
          Gender: formData.Gender,
          BirthOn: formData.BirthOn ? toLocalISOString(formData.BirthOn) : "",
          PhoneNumber: formData.PhoneNumber,
          Email: formData.Email,
          Address: formData.Address || "",
          JobTitleId: Number(selectedJobTitle),
          EmployeeOn: formData.EmployeeOn ? toLocalISOString(formData.EmployeeOn) : "",
          Status: Number(formData.Status),
        },
      ],
    };

    try {
      const response = await SaveEmployee(employeePayload);
      if (response.Code === 0) {
        await SaveAccessCard(accessCardPayload);

        setFormData(INITIAL_VALUES);
        setSelectedJobTitle("0");
        setErrors(INITIAL_ERRORS);
        setOriginalEmployeeNumber("");
        setSelectedMallId("0");
        setSelectedTenantId("0");
        setTenantOptions([]);

        await Swal.fire({
          icon: "success",
          title: "Saved Successfully!",
          text: "Employee data has been saved.",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        });

        navigate(-1);
      } else if (response.Code === 1) {
        Swal.fire({
          icon: "warning",
          title: "Error",
          text: response.Message || "Card Number not found",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "OK",
        });
      } else {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: response.Message || "Failed Save Employee",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    } catch {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed Save Employee",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
  };

  const handleDiscard = () => {
    setFormData(INITIAL_VALUES);
    setSelectedJobTitle("0");
    setErrors(INITIAL_ERRORS);
    setSelectedMallId("0");
    setSelectedTenantId("0");
    setTenantOptions([]);
  };

  const RequiredLabel = ({ children }: { children: string }) => (
    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
      {children} <span className="text-red-500">*</span>
    </label>
  );

  const OptionalLabel = ({ children }: { children: string }) => (
    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
      {children}
    </label>
  );

  const ErrorMsg = ({ msg }: { msg: string }) =>
    msg ? <p className="mt-1 text-xs text-red-500">{msg}</p> : null;

  return (
    <div className="lg:space-y-6 lg:p-6">
      <style>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px white inset !important;
          -webkit-text-fill-color: #111827 !important;
          transition: background-color 5000s ease-in-out 0s;
        }
        .dark input:-webkit-autofill,
        .dark input:-webkit-autofill:hover,
        .dark input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px #1f2937 inset !important;
          -webkit-text-fill-color: #f3f4f6 !important;
        }

        .react-datepicker-popper {
          z-index: 9999 !important;
        }

        .react-datepicker {
          font-family: inherit;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.05);
          overflow: hidden;
        }

        .react-datepicker__header {
          background-color: #2563eb;
          border-bottom: none;
          padding: 12px 0 8px;
        }

        .react-datepicker__current-month,
        .react-datepicker__day-name {
          color: #ffffff;
        }

        .react-datepicker__day-name {
          font-weight: 600;
          font-size: 0.75rem;
        }

        .react-datepicker__navigation-icon::before {
          border-color: #ffffff;
        }

        .react-datepicker__day {
          border-radius: 0.375rem;
          color: #111827;
          font-size: 0.875rem;
        }

        .react-datepicker__day:hover {
          background-color: #dbeafe;
          color: #1d4ed8;
          border-radius: 0.375rem;
        }

        .react-datepicker__day--selected,
        .react-datepicker__day--keyboard-selected {
          background-color: #2563eb !important;
          color: #ffffff !important;
          border-radius: 0.375rem;
          font-weight: 600;
        }

        .react-datepicker__day--today {
          font-weight: 700;
          color: #2563eb;
        }

        .react-datepicker__day--today.react-datepicker__day--selected {
          color: #ffffff;
        }

        .react-datepicker__day--outside-month {
          color: #9ca3af;
        }

        .react-datepicker__month-select,
        .react-datepicker__year-select {
          background-color: #1d4ed8;
          color: #ffffff;
          border: none;
          border-radius: 0.375rem;
          padding: 2px 6px;
          font-size: 0.875rem;
          cursor: pointer;
        }

        .react-datepicker__month-select:focus,
        .react-datepicker__year-select:focus {
          outline: none;
        }

        .dark .react-datepicker {
          background-color: #1f2937;
          border-color: #374151;
        }

        .dark .react-datepicker__day {
          color: #f3f4f6;
        }

        .dark .react-datepicker__day:hover {
          background-color: #1e3a5f;
          color: #93c5fd;
        }

        .dark .react-datepicker__day--outside-month {
          color: #6b7280;
        }

        .dark .react-datepicker__month-container {
          background-color: #1f2937;
        }
      `}</style>

      <div className="min-h-screen p-6 sm:p-8 rounded-xl bg-white dark:bg-gray-900">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-8 font-medium transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <OptionalLabel>Mall</OptionalLabel>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100" />
                <select
                  name="MallId"
                  value={selectedMallId}
                  onChange={handleMallChange}
                  disabled={mallOptions.length === 1}
                  className={`w-full border border-gray-200 dark:border-gray-700 rounded-lg px-10 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none ${
                    mallOptions.length === 1 ? "cursor-not-allowed" : ""
                  }`}
                  style={{
                    WebkitTextFillColor: "currentColor",
                    opacity: 1,
                  }}
                >
                  <option value="0">--- Choose One ---</option>
                  {mallOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {mallOptions.length > 1 && (
                  <ChevronLeft className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100 rotate-[-90deg] pointer-events-none" />
                )}
              </div>
            </div>

            <div>
              <OptionalLabel>Tenant</OptionalLabel>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100" />
                <select
                  name="TenantId"
                  value={selectedTenantId}
                  onChange={handleTenantChange}
                  disabled={selectedMallId === "0" || isTenantLoading || tenantOptions.length === 1}
                  className={`w-full border border-gray-200 dark:border-gray-700 rounded-lg px-10 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none ${
                    selectedMallId === "0" || isTenantLoading || tenantOptions.length === 1 ? "cursor-not-allowed" : ""
                  }`}
                  style={{
                    WebkitTextFillColor: "currentColor",
                    opacity: 1,
                  }}
                >
                  <option value="0">
                    {isTenantLoading
                      ? "Loading..."
                      : selectedMallId === "0"
                        ? "--- Select Mall First ---"
                        : "--- Choose One ---"}
                  </option>
                  {tenantOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {!(selectedMallId === "0" || isTenantLoading || tenantOptions.length <= 1) && (
                  <ChevronLeft className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100 rotate-[-90deg] pointer-events-none" />
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <RequiredLabel>Employee Card Number</RequiredLabel>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  name="EmployeeNumber"
                  value={formData.EmployeeNumber}
                  onChange={handleChange}
                  disabled={!!employeeId}
                  placeholder="eg.2025000*****"
                  className={
                    employeeId
                      ? `w-full bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-10 py-2.5 text-gray-400 dark:text-gray-500 cursor-not-allowed transition-all`
                      : errors.EmployeeNumber
                        ? inputErrorClass
                        : inputClass
                  }
                />
              </div>
              <ErrorMsg msg={errors.EmployeeNumber} />
            </div>

            <div>
              <OptionalLabel>Card Expired On</OptionalLabel>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100 z-10" />
                <DatePicker
                  wrapperClassName="w-full"
                  className={datePickerClass}
                  selected={formData.CardExpiredOn}
                  onChange={(date) =>
                    setFormData((prev) => ({ ...prev, CardExpiredOn: date }))
                  }
                  dateFormat="d MMMM yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  scrollableYearDropdown
                  yearDropdownItemNumber={20}
                  placeholderText={todayPlaceholder}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <RequiredLabel>First Name</RequiredLabel>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100" />
                    <input
                      type="text"
                      name="FirstName"
                      value={formData.FirstName}
                      onChange={handleChange}
                      placeholder="e.g. John"
                      className={errors.FirstName ? inputErrorClass : inputClass}
                    />
                  </div>
                  <ErrorMsg msg={errors.FirstName} />
                </div>

                <div>
                  <RequiredLabel>Last Name</RequiredLabel>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100" />
                    <input
                      type="text"
                      name="LastName"
                      value={formData.LastName}
                      onChange={handleChange}
                      placeholder="e.g. Doe"
                      className={errors.LastName ? inputErrorClass : inputClass}
                    />
                  </div>
                  <ErrorMsg msg={errors.LastName} />
                </div>
              </div>
            </div>

            <div>
              <RequiredLabel>Employee NIK</RequiredLabel>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100" />
                <input
                  type="text"
                  name="IDNumber"
                  value={formData.IDNumber}
                  onChange={handleChange}
                  placeholder="e.g. 3172XXXXXXXXXXXX"
                  className={errors.IDNumber ? inputErrorClass : inputClass}
                />
              </div>
              <ErrorMsg msg={errors.IDNumber} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <OptionalLabel>Full Name</OptionalLabel>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  name="FullName"
                  value={formData.FullName}
                  readOnly
                  className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-10 py-2.5 text-gray-400 dark:text-gray-500 cursor-not-allowed transition-all"
                />
              </div>
            </div>

            <div>
              <OptionalLabel>Gender</OptionalLabel>
              <div className="flex items-center gap-6 mt-2.5">
                <label className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="Gender"
                    value={1}
                    checked={formData.Gender === 1}
                    onChange={() => setFormData((prev) => ({ ...prev, Gender: 1 }))}
                    className="accent-blue-600"
                  />
                  Male
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="Gender"
                    value={0}
                    checked={formData.Gender === 0}
                    onChange={() => setFormData((prev) => ({ ...prev, Gender: 0 }))}
                    className="accent-blue-600"
                  />
                  Female
                </label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <OptionalLabel>Birth Date</OptionalLabel>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100 z-10" />
                <DatePicker
                  wrapperClassName="w-full"
                  className={datePickerClass}
                  selected={formData.BirthOn}
                  onChange={(date) =>
                    setFormData((prev) => ({ ...prev, BirthOn: date }))
                  }
                  dateFormat="d MMMM yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  scrollableYearDropdown
                  yearDropdownItemNumber={80}
                  maxDate={new Date()}
                  placeholderText={todayPlaceholder}
                />
              </div>
            </div>

            <div>
              <RequiredLabel>Phone Number</RequiredLabel>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100" />
                <input
                  type="tel"
                  name="PhoneNumber"
                  value={formData.PhoneNumber}
                  onChange={handleChange}
                  placeholder="e.g. 081234567890"
                  className={errors.PhoneNumber ? inputErrorClass : inputClass}
                />
              </div>
              <ErrorMsg msg={errors.PhoneNumber} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <RequiredLabel>Email</RequiredLabel>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100" />
                <input
                  type="email"
                  name="Email"
                  value={formData.Email}
                  onChange={handleChange}
                  placeholder="e.g. john.doe@email.com"
                  className={errors.Email ? inputErrorClass : inputClass}
                />
              </div>
              <ErrorMsg msg={errors.Email} />
            </div>

            <div>
              <OptionalLabel>Address</OptionalLabel>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100" />
                <input
                  type="text"
                  name="Address"
                  value={formData.Address}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <RequiredLabel>Job Title</RequiredLabel>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100" />
                <select
                  name="jobTitle"
                  value={selectedJobTitle}
                  onChange={(e) => {
                    setSelectedJobTitle(e.target.value);
                    setErrors((prev) => ({ ...prev, JobTitle: "" }));
                  }}
                  className={`w-full bg-white dark:bg-gray-800 border ${errors.JobTitle
                    ? "border-red-400 focus:ring-red-400"
                    : "border-gray-200 dark:border-gray-700 focus:ring-blue-500"
                    } rounded-lg px-10 py-2.5 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:border-transparent transition-all appearance-none`}
                >
                  <option value="0">-- Choose Job Title --</option>
                  <option value="10">Store Manager</option>
                  <option value="20">Staff</option>
                  {jobTitleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronLeft className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100 rotate-[-90deg] pointer-events-none" />
              </div>
              <ErrorMsg msg={errors.JobTitle} />
            </div>

            <div>
              <OptionalLabel>Employee On</OptionalLabel>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100 z-10" />
                <DatePicker
                  wrapperClassName="w-full"
                  className={datePickerClass}
                  selected={formData.EmployeeOn}
                  onChange={(date) =>
                    setFormData((prev) => ({ ...prev, EmployeeOn: date }))
                  }
                  dateFormat="d MMMM yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  scrollableYearDropdown
                  yearDropdownItemNumber={80}
                  placeholderText={todayPlaceholder}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <RequiredLabel>Employee Status</RequiredLabel>
              <div className="relative">
                <select
                  name="Status"
                  value={formData.Status}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setFormData((prev) => ({ ...prev, Status: val }));
                  }}
                  className={`w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none`}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
                <ChevronLeft className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 dark:text-gray-100 rotate-[-90deg] pointer-events-none" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-6">
            {!employeeId && (
              <button
                type="button"
                onClick={handleDiscard}
                className="px-8 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-all duration-200"
              >
                Discard Draft
              </button>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all duration-200"
            >
              {employeeId ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}